import type { WorkspaceMembersResult } from '@cbhq/identity-api-hooks/account-management/fetch';
import type {
  CoinbaseEngUsersAccountManagementApiApiPendingInvite,
  CoinbaseEngUsersAccountManagementApiApiWorkspaceMember,
} from '@cbhq/identity-api-hooks/account-management/types';
import { CoinbaseEngUsersAccountManagementApiApiRoleActivationState as RoleActivationState } from '@cbhq/identity-api-hooks/account-management/types';

import type {
  EntityRole,
  Member as TeamMember,
  MemberRole,
  MemberRolesForEntity,
} from './constants';
import type { PendingRole } from './gracePeriod';
import { parseTimestamp } from './gracePeriod';

type PendingInvite = CoinbaseEngUsersAccountManagementApiApiPendingInvite;
type ApiWorkspaceMember = CoinbaseEngUsersAccountManagementApiApiWorkspaceMember;

/** A GET /organization/v1/workspace/members member with its fields defaulted and its roles split by status. */
export type WorkspaceTeamMember = {
  userUuid: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  /** Org-level roles, e.g. workspace_org_admin. */
  orgRoles: MemberRole[];
  /** Legal-entity roles in effect, from `legalEntityRoles`. */
  effectiveRoles: EntityRole[];
  /** Legal-entity roles still in their grace period: SCHEDULED entries of `legalEntityRolesDetailed`. */
  pendingRoles: PendingRole[];
};

/** The pending invites to one email address, one per legal entity. */
export type WorkspaceTeamInvitee = {
  /** The first invite's email. The other invites match it case-insensitively. */
  email: string;
  invites: PendingInvite[];
};

/** GET /organization/v1/workspace/members, normalized and indexed for lookups. */
export type WorkspaceTeam = {
  /** Active members in API order. */
  members: WorkspaceTeamMember[];
  pendingInvites: PendingInvite[];
  membersById: ReadonlyMap<string, WorkspaceTeamMember>;
  /** Pending invites grouped by email, in API order. Invites without an email are left out. */
  invitees: WorkspaceTeamInvitee[];
  inviteesByInviteId: ReadonlyMap<string, WorkspaceTeamInvitee>;
  /** Keyed by `toEmailKey`. */
  inviteesByEmail: ReadonlyMap<string, WorkspaceTeamInvitee>;
};

/**
 * Normalizes one GET /organization/v1/workspace/members member. `legalEntityRoles`
 * lists only effective roles. A role still in its grace period appears only in
 * `legalEntityRolesDetailed` as a SCHEDULED detail, and becomes a pending role.
 * AWAITING_GRANT and CANCELLATION_PENDING details are left out. Entries without a
 * legal entity ID or role are skipped.
 */
export function normalizeWorkspaceMember(member: ApiWorkspaceMember): WorkspaceTeamMember {
  const effectiveRoles = (member.legalEntityRoles ?? []).flatMap(
    ({ legalEntityId, legalEntityName, roles }) =>
      legalEntityId
        ? (roles ?? []).map((role): EntityRole => ({ legalEntityId, legalEntityName, role }))
        : [],
  );

  const pendingRoles = (member.legalEntityRolesDetailed ?? []).flatMap(
    ({ legalEntityId, legalEntityName, rolesDetailed }) =>
      (legalEntityId ? rolesDetailed ?? [] : []).flatMap(
        ({ role, effectiveAt: rawEffectiveAt, activationState }): PendingRole[] => {
          const effectiveAt = parseTimestamp(rawEffectiveAt);
          const isScheduled =
            activationState === RoleActivationState.ROLE_ACTIVATION_STATE_SCHEDULED;
          return legalEntityId && role && effectiveAt && isScheduled
            ? [{ legalEntityId, legalEntityName, role, effectiveAt }]
            : [];
        },
      ),
  );

  return {
    userUuid: member.userUuid ?? '',
    firstName: member.firstName ?? '',
    lastName: member.lastName ?? '',
    email: member.email ?? '',
    avatarUrl: member.avatarUrl,
    orgRoles: member.roles ?? [],
    effectiveRoles,
    pendingRoles,
  };
}

/** "First Last", falling back to the email when both names are empty. */
export function getMemberDisplayName({
  firstName,
  lastName,
  email,
}: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): string {
  return [firstName, lastName].filter(Boolean).join(' ') || (email ?? '');
}

/** Every role the member holds or has pending, deduplicated. */
export function getMemberRoles({
  effectiveRoles,
  pendingRoles,
}: Pick<WorkspaceTeamMember, 'effectiveRoles' | 'pendingRoles'>): MemberRole[] {
  return [...new Set([...effectiveRoles, ...pendingRoles].map(({ role }) => role))];
}

/** Groups entity roles per legal entity, in order. */
export function groupRolesByEntity(entityRoles: readonly EntityRole[]): MemberRolesForEntity[] {
  const rolesByEntity = new Map<string, MemberRole[]>();
  for (const { legalEntityId, role } of entityRoles) {
    rolesByEntity.set(legalEntityId, [...(rolesByEntity.get(legalEntityId) ?? []), role]);
  }
  return [...rolesByEntity].map(([legalEntityId, roles]) => ({ legalEntityId, roles }));
}

/**
 * The roles per legal entity of a raw GET /organization/v1/workspace/members
 * member, for code that reads the cached response. Entries without an ID are skipped.
 */
export function getMemberRolesByEntity(
  member: Pick<ApiWorkspaceMember, 'legalEntityRoles'>,
): Map<string, MemberRole[]> {
  const rolesByEntity = new Map<string, MemberRole[]>();
  for (const { legalEntityId, roles } of member.legalEntityRoles ?? []) {
    if (legalEntityId) {
      rolesByEntity.set(legalEntityId, roles ?? []);
    }
  }
  return rolesByEntity;
}

/** The tray model of a member, with its effective roles only. */
export function toTeamMember(member: WorkspaceTeamMember): TeamMember {
  return {
    userUuid: member.userUuid,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    roles: [...new Set(member.effectiveRoles.map(({ role }) => role))],
    avatarUrl: member.avatarUrl,
  };
}

/** The case-insensitive key for matching emails. */
export function toEmailKey(email: string): string {
  return email.toLowerCase();
}

/** Groups pending invites by email, case-insensitively, in API order. Invites without an email are skipped. */
export function groupInvitesByEmail(
  pendingInvites: readonly PendingInvite[],
): WorkspaceTeamInvitee[] {
  const inviteesByEmail = new Map<string, WorkspaceTeamInvitee>();
  for (const invite of pendingInvites) {
    const invitee = invite.email ? inviteesByEmail.get(toEmailKey(invite.email)) : undefined;
    if (invitee) {
      invitee.invites.push(invite);
    } else if (invite.email) {
      inviteesByEmail.set(toEmailKey(invite.email), { email: invite.email, invites: [invite] });
    }
  }
  return [...inviteesByEmail.values()];
}

export function buildWorkspaceTeam(response: WorkspaceMembersResult | undefined): WorkspaceTeam {
  const members = (response?.members ?? []).map(normalizeWorkspaceMember);
  const pendingInvites = response?.pendingInvites ?? [];

  const membersById = new Map<string, WorkspaceTeamMember>();
  for (const member of members) {
    if (member.userUuid) {
      membersById.set(member.userUuid, member);
    }
  }

  const invitees = groupInvitesByEmail(pendingInvites);
  const inviteesByInviteId = new Map<string, WorkspaceTeamInvitee>();
  for (const invitee of invitees) {
    for (const { inviteId } of invitee.invites) {
      if (inviteId) {
        inviteesByInviteId.set(inviteId, invitee);
      }
    }
  }

  return {
    members,
    pendingInvites,
    membersById,
    invitees,
    inviteesByInviteId,
    inviteesByEmail: new Map(invitees.map((invitee) => [toEmailKey(invitee.email), invitee])),
  };
}
